import { cn } from "@/lib/utils";
import { tierNameToTier } from "@/redux/players/playersSlice";
import type { TierName } from "@repo/shared/types";
import { ReactNode } from "react";

import { Tier } from "../Tags/MetaRatingChip/Tier/Tier";

export type { TierName };

export type SelectableRowProps = {
  label: string;
  /** "row" = listbox item with trailing meta; "chip" = centered 2-up option. */
  variant?: "row" | "chip";
  /** Optional leading icon before the label (e.g. a chemstyle glyph). */
  icon?: ReactNode;
  /** Green chem indicator after the label: "++", or "+" for "plus". */
  plus?: boolean | "plus" | "plusPlus";
  /**
   * Optional trailing meta — both can be omitted (e.g. pickers where a rating
   * doesn't apply); the row stays selectable, it just renders no meta.
   */
  rating?: string | number;
  tier?: TierName;
  selected?: boolean;
  /** Not selectable; the row dims out and shows "–" in place of meta. */
  disabled?: boolean;
  onSelect?: () => void;
  className?: string;
};

/**
 * A single-select option (selection control, not an action button): represents
 * a value and holds selection state, emitting onSelect when picked.
 */
export const SelectableRow = ({
  label,
  variant = "row",
  icon,
  plus,
  rating,
  tier,
  selected,
  disabled,
  onSelect,
  className,
}: SelectableRowProps) => {
  const hasRating = rating != null && rating !== "";
  // Same badge as the meta rating chip
  const tierBadge = tier && tierNameToTier(tier, true);

  if (variant === "chip") {
    return (
      <button
        type="button"
        aria-pressed={selected}
        disabled={disabled}
        onClick={onSelect}
        title={label}
        className={cn(
          "flex items-center justify-center rounded-[10px] px-3 py-2.5",
          "text-[13.5px] font-medium leading-[1.15] transition-colors",
          selected
            ? "bg-primary text-on-primary"
            : "bg-neutral-750 text-neutral-300 hover:bg-neutral-700 hover:text-neutral-100",
          disabled && "text-neutral-500",
          className,
        )}
      >
        <span className="flex min-w-0 items-center justify-center gap-1.5">
          {icon && <span className="shrink-0">{icon}</span>}
          <span className="truncate">{label}</span>
          {plus && <Plus single={plus === "plus"} />}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
      title={label}
      className={cn(
        "flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-[7px]",
        "text-[13px] leading-[1.1] transition-[background,box-shadow] duration-100",
        disabled
          ? "bg-neutral-750/40 text-neutral-500"
          : selected
            ? "bg-blue-900/50 text-neutral-100 shadow-[inset_0_0_0_1px_var(--color-primary-border)]"
            : "bg-neutral-750 text-neutral-200 hover:bg-neutral-700 hover:text-neutral-100",
        className,
      )}
    >
      <span className="flex min-w-0 items-center gap-1.5 overflow-hidden">
        {icon && <span className="shrink-0">{icon}</span>}
        <span className="truncate">{label}</span>
        {plus && <Plus single={plus === "plus"} />}
      </span>
      <span className="flex shrink-0 items-center gap-[7px]">
        {disabled ? (
          <span>–</span>
        ) : (
          <>
            {hasRating && (
              <span className="font-semibold tabular-nums text-neutral-200">
                {rating}
              </span>
            )}
            {hasRating && tierBadge && <Tier tier={tierBadge} />}
          </>
        )}
      </span>
    </button>
  );
};

const Plus = ({ single }: { single?: boolean }) => (
  <span className="shrink-0 font-bold -tracking-[1px] text-green-400">
    {single ? "+" : "++"}
  </span>
);
